// ─── THEME / PRIMARY COLOUR ENGINE ─────────────────────────────────────────────
// The whole app palette is generated from ONE accent colour the user picks (see
// App.jsx's makeTheme() call). ACTIVE_BG_THEME is a mutable module-level
// singleton set once per ClarityApp render (via setActiveBgTheme) so the tone
// helpers (themeTone, isDarkBgTheme, ...) can re-tone category/habit colours to
// match the active background theme without prop-threading through every
// component. ES module named imports are live read-only bindings, so every
// other file that imports ACTIVE_BG_THEME sees updates automatically — only
// the render that owns the theme picker needs setActiveBgTheme().

// ─── PRIMARY COLOUR ENGINE ────────────────────────────────────────────────────
// The whole app palette is generated from ONE accent colour the user picks. The
// base canvas stays light (the "Blossom" light look — white cards, neutral bg);
// the primary colour tints the accents AND the text (app name, labels, buttons),
// while user-chosen colours (category / habit / note / week-block colours) are
// passed inline and stay untouched. Text colour on filled primary surfaces flips
// between dark/white via a WCAG-luminance check so light primaries stay readable.
export const _h2 = (h) => parseInt(h, 16);
export function hexToRgb(hex) {
  let h = (hex || "").replace("#", "");
  if (h.length === 3) h = h.split("").map(c => c + c).join("");
  if (h.length < 6) return { r: 212, g: 120, b: 154 };
  return { r: _h2(h.slice(0, 2)), g: _h2(h.slice(2, 4)), b: _h2(h.slice(4, 6)) };
}
export const _c2 = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
export const rgbToHex = (r, g, b) => `#${_c2(r)}${_c2(g)}${_c2(b)}`;
// Linear blend between two hex colours (t = 0 → a, t = 1 → b).
export function mix(a, b, t) {
  const A = hexToRgb(a), B = hexToRgb(b);
  return rgbToHex(A.r + (B.r - A.r) * t, A.g + (B.g - A.g) * t, A.b + (B.b - A.b) * t);
}
export const lighten = (hex, t) => mix(hex, "#ffffff", t);
export const darken  = (hex, t) => mix(hex, "#000000", t);
// WCAG relative luminance (0 = black, 1 = white).
export function luminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  const lin = (c) => { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}
// Best readable text colour to place ON a filled block of `hex`.
export const onColor = (hex) => (luminance(hex) > 0.42 ? "#2a2030" : "#ffffff");
// Hue-preserving pastel-ify: several theme accents are themselves dark/desaturated
// (slate, near-black, navy), so a plain lighten()/mix-toward-white on them still reads
// as grey once blended — there just isn't enough chroma left to carry a tint. Pulling
// the hue out via HSL and rebuilding at a fixed light-but-colourful saturation guarantees
// an actual pastel (not grey) for every theme, while still following that theme's hue.
export function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r: h = ((g - b) / d) % 6; break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s, l };
}
export function hslToRgb(h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;
  let [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
}
export function pastelize(hex, s = 0.62, l = 0.9) {
  const { r, g, b } = hexToRgb(hex);
  const { h } = rgbToHsl(r, g, b);
  const rgb = hslToRgb(h, s, l);
  return rgbToHex(rgb.r, rgb.g, rgb.b);
}
// Same hue-preserving idea as pastelize(), but rebuilt at a rich, readable-dark
// lightness instead of a light one — for use whenever the source colour is too
// dark/desaturated on its own to carry a background fill (would otherwise read
// as near-black rather than a moody, colourful dark shade).
export function vividDark(hex, s = 0.55, l = 0.32) {
  const { r, g, b } = hexToRgb(hex);
  const { h } = rgbToHsl(r, g, b);
  const rgb = hslToRgb(h, s, l);
  return rgbToHex(rgb.r, rgb.g, rgb.b);
}
// Same hue-preserving rebuild, but at a moderate saturation/lightness that still
// reads as a pastel (unlike vividDark's near-black-viable dark shade) — just a
// clearer, more saturated pastel than the raw picked colour so it doesn't wash
// out against a light card (e.g. habit checkboxes/counters).
export function vividPastel(hex, s = 0.6, l = 0.62) {
  const { r, g, b } = hexToRgb(hex);
  const { h } = rgbToHsl(r, g, b);
  const rgb = hslToRgb(h, s, l);
  return rgbToHex(rgb.r, rgb.g, rgb.b);
}
// Re-tone an arbitrary category colour toward the cool steel palette, so per-category
// card tints harmonise with the masculine theme while keeping a hint of their own hue.
// Kept deliberately light (0.20 not 0.66) — categories/habits must stay visually
// distinct and colourful (esp. in the habit tracker); this is a hint of theme, not a wash.
export const steelTone = (hex) => mix(hex || "#6e7d95", "#6e7d95", 0.20);
// Same idea for the warm "Peach" theme — pull every category/habit hue toward a
// soft coral so they all read as one warm family (peach, salmon, apricot, rose…)
// while still keeping a hint of their own hue so the cards stay distinguishable.
export const peachTone  = (hex) => mix(hex || "#e8916f", "#e8916f", 0.55);
export const purpleTone = (hex) => mix(hex || "#9F86C0", "#9F86C0", 0.55);
export const mochaTone  = (hex) => mix(hex || "#D3A376", "#D3A376", 0.55);
// Dusk and Frost lean slate/grey by design — same light-touch treatment as Steel so
// category colours never wash out to grey in the habit tracker.
export const duskTone   = (hex) => mix(hex || "#7E919F", "#7E919F", 0.20);
export const auroraTone = (hex) => mix(hex || "#cc8eb1", "#cc8eb1", 0.52);
export const lilacTone  = (hex) => mix(hex || "#C8B6FE", "#C8B6FE", 0.50);
export const velvetTone = (hex) => mix(hex || "#A1525F", "#A1525F", 0.55);
export const lagoonTone = (hex) => mix(hex || "#2f6fa8", "#2f6fa8", 0.55);
export const cloudTone  = (hex) => mix(hex || "#cf8fb0", "#cf8fb0", 0.52);
export const oceanTone  = (hex) => mix(hex || "#5483B3", "#5483B3", 0.55);
export const frostTone  = (hex) => mix(hex || "#857C91", "#857C91", 0.20);
export const sandTone   = (hex) => mix(hex || "#D7BDB0", "#D7BDB0", 0.25);
export const mistTone   = (hex) => mix(hex || "#99CDD8", "#99CDD8", 0.50);
export const opalTone  = (hex) => mix(hex || "#9AA6D8", "#9AA6D8", 0.30);
export const bloomTone  = (hex) => mix(hex || "#D9A08C", "#D9A08C", 0.30);
export const terraTone  = (hex) => mix(hex || "#A4624D", "#A4624D", 0.52);
export const glacierTone = (hex) => mix(hex || "#6A97C0", "#6A97C0", 0.55);
export const shoreTone   = (hex) => mix(hex || "#679391", "#679391", 0.25);
export const tideTone    = (hex) => mix(hex || "#297376", "#297376", 0.55);
// Active background theme — set during ClarityApp render so colour helpers can
// re-tone category/habit colours (checkboxes, graphs, labels) to match the theme
// without prop-threading through every component. Read synchronously in the same
// render pass, so children always see the current value.
export let ACTIVE_BG_THEME = "none";
export function setActiveBgTheme(v) { ACTIVE_BG_THEME = v; }
// Re-tone a habit/category colour to the active theme (steel on Steel, warm coral on Peach).
export const themeTone = (hex) =>
  ACTIVE_BG_THEME === "peach"   ? peachTone(hex || "#d4789a")
  : ACTIVE_BG_THEME === "purple"  ? purpleTone(hex || "#d4789a")
  : ACTIVE_BG_THEME === "mocha"   ? mochaTone(hex || "#d4789a")
  : ACTIVE_BG_THEME === "dusk"    ? duskTone(hex || "#d4789a")
  : ACTIVE_BG_THEME === "aurora"  ? auroraTone(hex || "#d4789a")
  : ACTIVE_BG_THEME === "lilac"   ? lilacTone(hex || "#d4789a")
  : ACTIVE_BG_THEME === "velvet"  ? velvetTone(hex || "#d4789a")
  : ACTIVE_BG_THEME === "lagoon"  ? lagoonTone(hex || "#d4789a")
  : ACTIVE_BG_THEME === "cloud"   ? cloudTone(hex || "#d4789a")
  : ACTIVE_BG_THEME === "ocean"   ? oceanTone(hex || "#d4789a")
  : ACTIVE_BG_THEME === "frost"   ? frostTone(hex || "#d4789a")
  : ACTIVE_BG_THEME === "sand"    ? sandTone(hex || "#d4789a")
  : ACTIVE_BG_THEME === "mist"    ? mistTone(hex || "#d4789a")
  : ACTIVE_BG_THEME === "opal"   ? opalTone(hex || "#d4789a")
  : ACTIVE_BG_THEME === "bloom"   ? bloomTone(hex || "#d4789a")
  : ACTIVE_BG_THEME === "terra"   ? terraTone(hex || "#d4789a")
  : ACTIVE_BG_THEME === "glacier" ? glacierTone(hex || "#d4789a")
  : ACTIVE_BG_THEME === "shore"   ? shoreTone(hex || "#d4789a")
  : ACTIVE_BG_THEME === "tide"    ? tideTone(hex || "#d4789a")
  : (hex || "#d4789a");
// Same, but darkened a touch so coloured text/labels stay clearly readable.
export const themeToneText = (hex) => darken(themeTone(hex),
  ACTIVE_BG_THEME === "masculine" ? 0.18 : ACTIVE_BG_THEME === "peach" ? 0.24 : ACTIVE_BG_THEME === "purple" ? 0.24 : ACTIVE_BG_THEME === "mocha" ? 0.24 : ACTIVE_BG_THEME === "dusk" ? 0.20 : ACTIVE_BG_THEME === "aurora" ? 0.22 : ACTIVE_BG_THEME === "lilac" ? 0.24 : ACTIVE_BG_THEME === "velvet" ? 0.22 : ACTIVE_BG_THEME === "lagoon" ? 0.22 : ACTIVE_BG_THEME === "cloud" ? 0.24 : ACTIVE_BG_THEME === "ocean" ? 0.22 : ACTIVE_BG_THEME === "frost" ? 0.22 : ACTIVE_BG_THEME === "sand" ? 0.26 : ACTIVE_BG_THEME === "mist" ? 0.22 : ACTIVE_BG_THEME === "opal" ? 0.22 : ACTIVE_BG_THEME === "bloom" ? 0.28 : ACTIVE_BG_THEME === "terra" ? 0.22 : ACTIVE_BG_THEME === "glacier" ? 0.22 : ACTIVE_BG_THEME === "shore" ? 0.22 : ACTIVE_BG_THEME === "tide" ? 0.22 : 0);
// Themes whose background gradient reads dark overall (near-black top stop) — used
// to pick a plain white-or-black label colour instead of a category-tinted one.
export const DARK_BG_THEMES = new Set(["dusk", "purple", "velvet", "ocean", "glacier", "tide"]);
// Dark mode counts too: everything that asks "is the page behind me dark?" (text and
// icons sitting straight on the background) answers the same way for it.
export let ACTIVE_DARK_MODE = false;
export function setActiveDarkMode(v) { ACTIVE_DARK_MODE = !!v; }
export const isDarkBgTheme = () => ACTIVE_DARK_MODE || DARK_BG_THEMES.has(ACTIVE_BG_THEME);
// True only for dark mode itself (cards are dark too), not the dark gradients.
export const isDarkMode = () => ACTIVE_DARK_MODE;
// Text / icon colour for a category label (category pills in the habit and
// Planner forms). Light mode: the hue at a rich dark tone (vividDark). Dark
// mode: a light, softened tint of the hue, so it reads on a dark chip instead
// of a saturated colour that sinks into it (Jade, 2026-10-17).
export function categoryInk(hex) {
  if (!ACTIVE_DARK_MODE) return vividDark(hex);
  return lightCategoryInk(hex);
}
// A light pastel of a category colour, readable on any dark surface (dark mode,
// or a see-through card on a dark gradient background).
export function lightCategoryInk(hex) {
  const { r, g, b } = hexToRgb(hex && hex.startsWith("#") ? hex : "#d4789a");
  const { h, s } = rgbToHsl(r, g, b);
  const rgb = hslToRgb(h, Math.min(Math.max(s, 0.35), 0.62), 0.78);
  return rgbToHex(rgb.r, rgb.g, rgb.b);
}
// Darken a colour until it reads clearly on a white background (for accent text).
export function readableOnWhite(hex) {
  let out = hex;
  let guard = 0;
  while (luminance(out) > 0.34 && guard < 12) { out = darken(out, 0.12); guard++; }
  return out;
}
// Deepen a chosen accent enough to stay visible on the Default theme's white canvas.
// Light pastel picks (e.g. the Default accent swatch grid) would otherwise derive
// near-white borders and washed-out icons/buttons. Only genuinely light colours are
// pulled down (via HSL, hue preserved) to a readable mid-tone; mid-tone accents like
// the classic pink pass straight through unchanged.
// Stays pastel on purpose — capped saturation + a fairly light target lightness —
// so the applied accent still reads as the soft swatch the user picked rather than
// the saturated jewel-tone the old l:0.55/min-sat:0.42 rebuild produced.
export function deepenAccent(hex) {
  const { r, g, b } = hexToRgb(hex);
  const { h, s, l } = rgbToHsl(r, g, b);
  if (l <= 0.72) return hex;
  const rgb = hslToRgb(h, Math.min(Math.max(s, 0.30), 0.48), 0.74);
  return rgbToHex(rgb.r, rgb.g, rgb.b);
}
// Generate the full theme object from a single primary accent colour.
export function makeTheme(primary) {
  const p = (primary && primary.startsWith("#")) ? primary : "#d4789a";
  const deep   = darken(p, 0.20);
  const light  = lighten(p, 0.20);
  const accent = readableOnWhite(p);          // app name / accent text on white
  const { r, g, b } = hexToRgb(p);
  const rgba = (a) => `rgba(${r},${g},${b},${a})`;
  return {
    name:         "Custom",
    primary:      p,
    primaryDeep:  deep,
    primaryLight: light,
    onPrimary:    onColor(p),
    // Light base canvas — kept neutral/white so only accents + text take the hue.
    bg:           "#ffffff",
    bgSoft:       "#ffffff",
    bgCard:       "#ffffff",
    bgRaised:     "#ffffff",
    bgActive:     lighten(p, 0.88),
    // Borders derive from the readable (darkened-if-light) accent rather than the raw
    // primary, then lighten less than before — on the Default theme's white canvas the
    // old lighten(p,0.70/0.78) washed out to near-invisible, especially for light picks.
    // (Every gradient theme overrides --c-border in App.jsx, so this only shifts Default.)
    border:       lighten(accent, 0.58),
    borderLight:  lighten(accent, 0.70),
    borderMid:    rgba(0.35),
    // Calendar week-view grid lines — kept clearly visible (darker than the soft borders).
    gridLine:     rgba(0.92),
    gridLineSoft: rgba(0.62),
    // Deep → light → fades into the surface, for the masked gradient ring.
    borderGrad:   `linear-gradient(150deg, ${deep} 0%, ${p} 30%, ${lighten(p, 0.72)} 75%, ${lighten(p, 0.92)} 100%)`,
    sidebar:      `linear-gradient(180deg, #ffffff 0%, ${lighten(p, 0.92)} 100%)`,
    topbar:       "#ffffff",
    scrollThumb:  lighten(p, 0.45),
    // Text takes a gentle hue of the primary but is darkened to stay readable.
    text:         mix(accent, "#2c2433", 0.82),
    textMid:      mix(accent, "#6a5f72", 0.50),
    textSoft:     mix(accent, "#b3a8bd", 0.45),
    textAccent:   accent,
    shadow:       rgba(0.12),
    shadowBtn:    `rgba(${hexToRgb(deep).r},${hexToRgb(deep).g},${hexToRgb(deep).b},0.35)`,
    gradient:     `linear-gradient(135deg, ${lighten(p, 0.86)}, ${lighten(p, 0.78)})`,
  };
}
export const DEFAULT_PRIMARY = "#d4789a";   // Blossom pink = the light-mode default

// ── Dark mode (Jade, 2026-10-17) ────────────────────────────────────────────
// From the research round: a near-black grey page (Material's #121212) rather
// than pure black, surfaces that get LIGHTER as they come forward (page < card
// < sheet/popup) instead of relying on shadows, off-white text, and the accent
// lifted to a softer, lighter tone so it stays pastel instead of glowing.
// "black" is the OLED option: pure black page, cards one step up.
export const DARK_SURFACES = {
  dark:  { bg: "#121212", card: "#1e1e1e", raised: "#2a2a2a", active: "#262626" },
  black: { bg: "#000000", card: "#121212", raised: "#1e1e1e", active: "#1a1a1a" },
};
// The Default accent (often a light pastel) as a soft, light tone that reads on
// dark grey: hue kept, saturation capped, lightness pinned around 72%.
export function darkAccent(hex) {
  const { r, g, b } = hexToRgb(hex && hex.startsWith("#") ? hex : DEFAULT_PRIMARY);
  const { h, s } = rgbToHsl(r, g, b);
  const rgb = hslToRgb(h, Math.min(Math.max(s, 0.35), 0.62), 0.72);
  return rgbToHex(rgb.r, rgb.g, rgb.b);
}
// Same keys as makeTheme, so every var(--c-*) consumer follows without changes.
export function makeDarkTheme(accentHex, style = "dark") {
  const S = DARK_SURFACES[style] || DARK_SURFACES.dark;
  const p = darkAccent(accentHex);
  const { r, g, b } = hexToRgb(p);
  const rgba = (a) => `rgba(${r},${g},${b},${a})`;
  return {
    name:         style === "black" ? "Black" : "Dark",
    dark:         true,
    primary:      p,
    primaryDeep:  darken(p, 0.18),
    primaryLight: lighten(p, 0.18),
    onPrimary:    "#1a1418",
    bg:           S.bg,
    bgSoft:       S.bg,
    bgCard:       S.card,
    bgRaised:     S.raised,
    // Also the surface of the add/edit panels (they paint with --c-bg-active), so
    // it is a near-neutral raised grey, not a 20% accent wash: dialogs on dark are
    // a lighter neutral surface (Material "surface container high"). Jade saw an
    // orange accent turn every panel brown, 2026-10-17.
    bgActive:     mix(p, S.raised, 0.92),
    border:       "rgba(255,255,255,0.12)",
    borderLight:  "rgba(255,255,255,0.08)",
    borderMid:    "rgba(255,255,255,0.20)",
    gridLine:     "rgba(255,255,255,0.32)",
    gridLineSoft: "rgba(255,255,255,0.2)",
    borderGrad:   `linear-gradient(150deg, ${p} 0%, ${rgba(0.5)} 60%, transparent 100%)`,
    sidebar:      S.card,
    topbar:       S.bg,
    scrollThumb:  "rgba(255,255,255,0.22)",
    text:         "#ece6ee",
    textMid:      "#c4bcc9",
    textSoft:     "#948b9a",
    textAccent:   p,
    shadow:       "rgba(0,0,0,0.45)",
    shadowBtn:    "rgba(0,0,0,0.5)",
    gradient:     `linear-gradient(135deg, ${mix(p, S.card, 0.78)}, ${mix(p, S.card, 0.68)})`,
  };
}

// Full-bleed background gradients for the app-theme picker (cards stay light).
// Both run medium→light top-to-bottom so dark card text stays readable on top.
export const PASTEL_BG    = "linear-gradient(180deg, #b1a5d4 0%, #c4add1 20%, #d9b5cd 40%, #efc0ca 60%, #f8d0c8 80%, #fde3d7 100%)";
export const MASCULINE_BG = "linear-gradient(180deg, #7d8aa3 0%, #8a96ae 20%, #9ca7bc 40%, #b2bbcb 60%, #cad0db 80%, #e6e9ee 100%)";
// Warm "Peach" canvas — soft apricot at the top melting down into blush/salmon.
// Uses all five palette tones, ordered darkest → lightest (#FEB4A7, #FFC69E, #FED5CD, #FADBC6, #F8EDEB).
export const PEACH_BG  = "linear-gradient(180deg, #FEB4A7 0%, #FFC69E 25%, #FED5CD 50%, #FADBC6 75%, #F8EDEB 100%)";
export const PURPLE_BG = "linear-gradient(180deg, #231942 0%, #5E548E 30%, #9F86C0 56%, #BE95C4 78%, #E0B1CB 100%)";
export const MOCHA_BG  = "linear-gradient(180deg, #3E2522 0%, #8C6E63 28%, #D3A376 54%, #FFE0B2 76%, #FFF2DF 100%)";
export const DUSK_BG   = "linear-gradient(180deg, #191D23 0%, #57707A 26%, #7E919F 46%, #979DAB 64%, #C5BAC4 82%, #DEDCDC 100%)";
export const LILAC_BG  = "linear-gradient(180deg, #BDCFFF 0%, #B8C0FF 25%, #C8B6FE 50%, #E7C5FF 75%, #FED5FF 100%)";
export const VELVET_BG = "linear-gradient(180deg, #140E1C 0%, #462037 22%, #683A46 44%, #A1525F 64%, #C5757C 82%, #F9AAAD 100%)";
// Lagoon — oceanic mesh gradient (same blurred-blob technique as Aurora). The
// mesh lives on a fixed, blurred .lagoon-main::before layer; the root just needs
// the neutral base colour underneath. Palette: #002B8C #4B0082 #87CEEB #E6E6FA #006982.
export const LAGOON_BG = "#dbe4f1";
// Cloud — dreamy pastel mesh (same blurred-blob technique as Aurora/Lagoon). Mesh
// lives on a fixed blurred .cloud-main::before; root just needs the base colour.
// Palette: #F8E0D0 #F5CAC8 #E7B7C5 #CBAFC7 #C2C4DC.
export const CLOUD_BG = "#f3e3e6";
// Ocean — deep navy-to-sky linear gradient. Palette: #021024 #052659 #5483B3 #7DA0CA #C1E8FF.
export const OCEAN_BG = "linear-gradient(180deg, #021024 0%, #052659 25%, #5483B3 55%, #7DA0CA 78%, #C1E8FF 100%)";
// Frost — winter dusk mesh (blurred-blob technique). Palette: #525871 #857C91 #CD9FA0 #F2C1A3 #F8CCAA.
export const FROST_BG = "#f0e6e4";
// Sand — warm beige-neutral mesh (blurred-blob technique). Palette: #D7BDB0 #E3D5CA #F5EBE1 #D6CCC2 #EDEEE9.
export const SAND_BG = "#ece2da";
// Mist — serene blue-sage-pink mesh (blurred-blob technique). Palette: #99CDD8 #DAEBE3 #FDE8D3 #F3C3B2 #CFD6C4 #657166.
export const MIST_BG = "#dde8e6";
// Opal — pearly pink, aqua & lilac mesh (blurred-blob technique). Palette: #F3B9CB #FFDCCB #FDF0F9 #AFEBF3 #8ED5F3 #B5A2D4.
export const OPAL_BG = "#f6eefa";
// Bloom — peach, coral & butter flower mesh (blurred-blob technique). Palette: #FAEBCB #F9A58F #FCB588 #FBD78E #E1DE9B.
export const BLOOM_BG = "#fbeed3";
// Terra — earthy Mediterranean mesh (blurred-blob technique). Palette: #A1B5A8 #CBB093 #DFA477 #A4624D #F5DFC5.
export const TERRA_BG = "#e3d3bf";
// Glacier — deep pine-to-sunbeam linear gradient. Palette: #152026 #1B3D59 #6A97C0 #B3D5F1 #D4EEF8 #F3EED8.
export const GLACIER_BG = "linear-gradient(180deg, #152026 0%, #1B3D59 22%, #6A97C0 48%, #B3D5F1 70%, #D4EEF8 85%, #F3EED8 100%)";
// Shore — coastal teal-sage mesh (blurred-blob technique). Palette: #597F86 #679391 #87B2B0 #99B4AE #ACB8AE #ADB1A5.
export const SHORE_BG = "#c8d8d6";
// Tide — deep midnight-teal-to-ice linear gradient. Palette: #013137 #215E68 #297376 #5C9396 #C1D9DE.
export const TIDE_BG = "linear-gradient(180deg, #013137 0%, #215E68 26%, #297376 50%, #5C9396 75%, #C1D9DE 100%)";
// Aurora — true fluid mesh gradient via the blurred-blob technique (the method
// dedicated mesh-gradient tools actually export). A few solid colour stops are
// painted on a fixed full-screen layer (.aurora-mesh, see CSS) and a big
// filter: blur() melts them into one seamless wash. Because the blur is uniform
// in every direction it looks identical on any aspect ratio — no mobile smear.
// The root canvas just needs the neutral base colour underneath that layer.
export const AURORA_BG = "#e2d8e4";

// ── System-bar strip colours ────────────────────────────────────────────────
// The native bar tint (src/capacitor/clarityBars.js) needs the colour the page
// actually shows at its very top and very bottom edge. For the themes defined
// above as a real top-to-bottom gradient that's just the first and last stop,
// which barColorsFor() reads straight off the string.
//
// These eight can't be read that way. Their *_BG is only a flat base colour;
// what you actually see is a blurred stack of radial gradients painted over it
// by the `.<theme>-main::before` rules in App.jsx. Taking the base colour
// tints the strips a shade the page never shows anywhere — Mist's base is a
// pale #dde8e6, while its top edge really renders as a much deeper #B2D3D5.
//
// So these are measured rather than guessed: each `.<theme>-main::before` rule
// was rendered headlessly at a 360x760 phone viewport and its edge colours
// sampled. One wrinkle worth knowing before re-measuring: the mesh is a
// blurred element, so its own bottom edge decays back to the flat base colour
// over roughly the last 12% of a standalone render. On device that tail falls
// off-screen — `position: fixed` anchors the mesh to a taller layout viewport
// than a plain headless page has — so sampling the literal last row gives the
// base colour again and puts the band right back. Sample the last row where
// the mesh still differs from the base instead. Re-measure the same way if any
// of these radial-gradient stacks change.
export const MESH_BAR_COLORS = {
  aurora: { top: "#A9A3C5", bottom: "#C3BFD0" },
  lagoon: { top: "#4E5AA8", bottom: "#4999B2" },
  cloud:  { top: "#F5DCD4", bottom: "#D6C8D8" },
  frost:  { top: "#958E9E", bottom: "#EFCBB9" },
  sand:   { top: "#DFCABF", bottom: "#E1D9D0" },
  mist:   { top: "#B1D1D3", bottom: "#E8D3C7" },
  bloom:  { top: "#FAB799", bottom: "#EAE3AD" }, // sampled from a headless render at 393x852
  opal:   { top: "#BFBADF", bottom: "#F6E6F0" }, // sampled from a headless render at 393x852
  terra:  { top: "#BBBAA6", bottom: "#C79B85" },
  shore:  { top: "#7C9B9D", bottom: "#A9C0BB" },
};

// Top/bottom edge colours for `bgTheme`, or null if neither route yields one.
export function barColorsFor(bgTheme, rootBg) {
  const mesh = MESH_BAR_COLORS[bgTheme];
  if (mesh) return mesh;
  const hexes = String(rootBg || "").match(/#[0-9a-fA-F]{3,8}/g);
  if (!hexes || !hexes.length) return null;
  return { top: hexes[0], bottom: hexes[hexes.length - 1] };
}
// App-theme options for the settings picker (single container, preview buttons).
// Each theme drives the app's primary colour (buttons, labels, topbar, etc.).
export const APP_THEME_OPTIONS = [
  { id: "none",      label: "Default",   primary: "#d4789a", preview: "#ffffff",                                                           chip: "#f3eef6",            desc: "Clean white canvas with soft pink accents. The classic Clarity look." },
  { id: "pastel",    label: "Pastel",    primary: "#d4789a", preview: "linear-gradient(160deg,#b1a5d4,#d9b5cd,#f8d0c8,#fde3d7)",            chip: "#fdfbff",            desc: "Soft pink-lavender gradient 🌸 Pink accents, cards stay white." },
  { id: "dusk",      label: "Dusk",      primary: "#57707A", preview: "linear-gradient(160deg,#191D23,#57707A,#979DAB,#DEDCDC)",              chip: "#f8f8f9",            desc: "Moody slate-to-mist gradient 🌫️ Cool teal-grey accents. Calm and understated. Cards stay light." },
  { id: "peach",     label: "Peach",     primary: "#e0795e", preview: "linear-gradient(160deg,#FEB4A7,#FFC69E,#FED5CD,#FADBC6)",            chip: "#fffaf8",            desc: "Warm peach-to-blush gradient 🍑 Soft coral accents. Cosy and sun-kissed. Cards stay light." },
  { id: "terra",     label: "Terra",     primary: "#A4624D", preview: "linear-gradient(135deg,#A1B5A8,#CBB093,#DFA477,#A4624D,#F5DFC5)",               chip: "#faf2e8",            desc: "Earthy Mediterranean mesh 🏺 Sage, khaki & terracotta blobs. Warm and grounded. Cards stay light." },
  { id: "purple",    label: "Purple",    primary: "#9F86C0", preview: "linear-gradient(160deg,#E0B1CB,#BE95C4,#9F86C0,#5E548E)",            chip: "#f9f7fd",            desc: "Dreamy purple-to-midnight gradient 💜 Soft lavender accents. Elegant and atmospheric. Cards stay light.", premium: true },
  { id: "velvet",    label: "Velvet",    primary: "#A1525F", preview: "linear-gradient(160deg,#140E1C,#462037,#A1525F,#F9AAAD)",               chip: "#fff7f8",            desc: "Moonlit plum-to-rose gradient 🌙 Deep rosewood accents. Dramatic and romantic. Cards stay light.", premium: true },
  { id: "cloud",     label: "Cloud",     primary: "#c97fa3", preview: "linear-gradient(135deg,#F8E0D0,#F5CAC8,#E7B7C5,#CBAFC7,#C2C4DC)",        chip: "#fdf6f4",            desc: "Dreamy cotton-candy sky mesh ☁️ Soft rose accents. Airy pastel clouds at dusk. Cards stay light.", premium: true },
  { id: "mist",      label: "Mist",      primary: "#657166", preview: "linear-gradient(135deg,#99CDD8,#DAEBE3,#FDE8D3,#F3C3B2,#CFD6C4,#657166)",          chip: "#f0f6f5",            desc: "Serene sky-sage-blush mesh 🌫️ Soft blue, mint & dusty pink blobs. Dreamy and airy. Cards stay light.", premium: true },
  { id: "opal",      label: "Opal",      primary: "#6B78C4", preview: "radial-gradient(circle at 12% 12%,#B5A2D4 0,transparent 55%),radial-gradient(circle at 88% 14%,#8ED5F3 0,transparent 48%),radial-gradient(circle at 50% 52%,#F3B9CB 0,transparent 55%),radial-gradient(circle at 10% 90%,#FFDCCB 0,transparent 50%),radial-gradient(circle at 90% 88%,#AFEBF3 0,transparent 45%),#FDF0F9",               chip: "#faf5fc",            desc: "Pearly pastel mesh 🐚 Lilac, sky blue, rose, aqua & peach blobs with violet accents. Shimmery and soft. Cards stay light.", premium: true },
  { id: "bloom",     label: "Bloom",     primary: "#B8553F", preview: "linear-gradient(135deg,#F9A58F,#FCB588,#FBD78E,#FAEBCB,#E1DE9B)",                       chip: "#fdf6e8",            desc: "Spring flower mesh 🌷 Coral, peach, butter, cream & soft green blobs with terracotta accents. Warm and fresh. Cards stay light.", premium: true },
];
export const themePrimary = (id) => (APP_THEME_OPTIONS.find(o => o.id === id) || APP_THEME_OPTIONS[0]).primary;
